'use client'

// Timeline Gantt task-level (untuk supervisor) — advanced tapi mudah dipahami.
// Group by project/dev, zoom minggu/bulan, bar progress, garis hari ini.
import { useMemo, useRef, useState } from 'react'
import { FolderKanban, Users } from 'lucide-react'
import type { Task } from '@/lib/types'
import { isoDay, diffDays, addDays, type RiskCtx } from '@/lib/dev-sprint'
import { useToast } from '@/context/ToastContext'
import type { BoardData, Filters, DevDerived } from './types'
import { taskMatchesFilters } from './filter'
import { STATUS_META, Avatar } from './ui'

const norm = (x?: string | null) => (x ?? '').trim().toLowerCase()
const UNASSIGNED = '__unassigned__'
const fmtDay = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

const PROGRESS: Record<string, number> = { 'not-started': 0.08, 'in-progress': 0.5, done: 1 }
const LEFT_W = 220
const DRAG_THRESHOLD = 6 // px minimal sebelum dianggap seret (biar klik kecil tak menggeser deadline)

interface Props {
  board: BoardData
  derived: DevDerived
  riskCtx: RiskCtx
  filters: Filters
  today: string
  isLead: boolean
  onOpenTask: (id: string) => void
  onChanged: () => void
}

export function GanttTimeline({ board, derived, riskCtx, filters, today, isLead, onOpenTask, onChanged }: Props) {
  const { statusOf, projById } = derived
  const { toast } = useToast()
  const [groupBy, setGroupBy] = useState<'project' | 'dev'>('project')
  const [zoom, setZoom] = useState<'week' | 'month'>('week')
  const pxPerDay = zoom === 'week' ? 26 : 9

  // Drag bar (lead) → geser deadline. deltaDays dari perpindahan pointer.
  const [drag, setDrag] = useState<{ id: string; startX: number; deltaDays: number } | null>(null)
  const didDrag = useRef(false)
  const [busy, setBusy] = useState(false)

  const finishDrag = async (t: Task) => {
    const dd = drag?.deltaDays ?? 0
    setDrag(null)
    if (!dd || busy) return
    const cur = isoDay(t.dueDate)
    if (!cur) return
    setBusy(true)
    try {
      const res = await fetch(`/api/dev/tasks/${t.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dueDate: addDays(cur, dd) }),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal')
      onChanged()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    } finally { setBusy(false) }
  }

  const visible = board.tasks.filter((t) => taskMatchesFilters(t, filters, derived, riskCtx))

  // Rentang tanggal: min start (createdAt) .. max end (dueDate|createdAt), + padding.
  const { rangeStart, totalDays } = useMemo(() => {
    let min = today, max = today
    for (const t of visible) {
      const s = isoDay(t.createdAt) || today
      const e = isoDay(t.dueDate) || s
      if (s < min) min = s
      if (e > max) max = e
    }
    const start = addDays(min, -3)
    const end = addDays(max, 4)
    return { rangeStart: start, totalDays: Math.max(14, diffDays(start, end) + 1) }
  }, [visible, today])

  const width = totalDays * pxPerDay
  const xOf = (iso: string) => diffDays(rangeStart, iso) * pxPerDay

  // Ticks header
  const ticks: { left: number; label: string }[] = []
  if (zoom === 'week') {
    // mulai dari Senin pertama >= rangeStart
    let d = rangeStart
    // Parse UTC (Z) agar konsisten dengan getUTCDay — hindari geser hari di TZ WIB.
    const dow = (new Date(d + 'T00:00:00Z').getUTCDay() + 6) % 7
    d = addDays(d, dow === 0 ? 0 : 7 - dow)
    for (; diffDays(rangeStart, d) < totalDays; d = addDays(d, 7)) ticks.push({ left: xOf(d), label: fmtDay(d) })
  } else {
    let d = rangeStart.slice(0, 8) + '01'
    for (; diffDays(rangeStart, d) < totalDays; ) {
      ticks.push({ left: xOf(d), label: `${MONTHS[+d.slice(5, 7) - 1]} ${d.slice(0, 4)}` })
      const y = +d.slice(0, 4), m = +d.slice(5, 7)
      d = (m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`) + '-01'
    }
  }

  // Grup baris
  type Row = { header: { name: string; color?: string; dev?: string } | null; task: Task | null }
  const rows: Row[] = []
  if (groupBy === 'project') {
    for (const p of board.projects) {
      const ts = visible.filter((t) => t.projectId === p.id)
      if (!ts.length) continue
      rows.push({ header: { name: p.name, color: p.color }, task: null })
      ts.sort((a, b) => (isoDay(a.dueDate) || '9999').localeCompare(isoDay(b.dueDate) || '9999'))
      for (const t of ts) rows.push({ header: null, task: t })
    }
  } else {
    const groups: { name: string; dev: string | null }[] = board.team.filter((m) => m.username).map((m) => ({ name: m.name, dev: m.username }))
    groups.push({ name: 'Belum di-assign', dev: null })
    for (const g of groups) {
      const key = g.dev ? norm(g.dev) : UNASSIGNED
      const ts = visible.filter((t) => (norm(t.assignee) || UNASSIGNED) === key)
      if (!ts.length) continue
      rows.push({ header: { name: g.name, dev: g.dev ?? undefined }, task: null })
      ts.sort((a, b) => (isoDay(a.dueDate) || '9999').localeCompare(isoDay(b.dueDate) || '9999'))
      for (const t of ts) rows.push({ header: null, task: t })
    }
  }

  const todayX = xOf(today)

  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EDEBE5] flex-wrap">
        <Toggle value={groupBy} onChange={setGroupBy} opts={[{ v: 'project', label: 'Per Project', Icon: FolderKanban }, { v: 'dev', label: 'Per Dev', Icon: Users }]} />
        <div className="w-px h-5 bg-[#E7E5DF]" />
        <Toggle value={zoom} onChange={setZoom} opts={[{ v: 'week', label: 'Minggu' }, { v: 'month', label: 'Bulan' }]} />
        <div className="ml-auto flex items-center gap-3 text-[11px] text-[#8B8A85]">
          {(['not-started', 'in-progress', 'done'] as const).map((s) => (
            <span key={s} className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: STATUS_META[s].dot }} />{STATUS_META[s].label}</span>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-16 text-center text-[13px] text-[#AEADA8]">Tidak ada task untuk ditampilkan.</div>
      ) : (
        <div className="overflow-x-auto">
          <div style={{ minWidth: LEFT_W + width }}>
            {/* Header ticks */}
            <div className="flex sticky top-0 z-10 bg-[#FAF9F6] border-b border-[#E7E5DF]">
              <div style={{ width: LEFT_W }} className="flex-shrink-0 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-[#8B8A85] border-r border-[#E7E5DF]">
                {groupBy === 'project' ? 'Project / Task' : 'Dev / Task'}
              </div>
              <div className="relative" style={{ width }}>
                {ticks.map((t, i) => (
                  <div key={i} className="absolute top-0 h-full border-l border-[#EDEBE5] pl-1 text-[10px] text-[#AEADA8] py-2 whitespace-nowrap" style={{ left: t.left }}>{t.label}</div>
                ))}
              </div>
            </div>

            {/* Rows */}
            <div className="relative">
              {/* today line across body */}
              {todayX >= 0 && todayX <= width && (
                <div className="absolute top-0 bottom-0 z-0 w-px bg-[#D4A843]" style={{ left: LEFT_W + todayX }} />
              )}
              {rows.map((row, idx) => {
                if (row.header) {
                  return (
                    <div key={`h${idx}`} className="flex items-center gap-2 bg-[#F7F6F2] border-b border-[#EDEBE5] px-3 py-1.5" style={{ paddingLeft: 12 }}>
                      {row.header.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: row.header.color }} />}
                      {row.header.dev && <Avatar name={row.header.name} size={18} />}
                      <span className="text-[12px] font-bold text-[#1A1916] truncate">{row.header.name}</span>
                    </div>
                  )
                }
                const t = row.task as Task
                const st = statusOf(t)
                const s = isoDay(t.createdAt) || today
                const e = isoDay(t.dueDate) || s
                const left = xOf(s)
                const barW = Math.max(pxPerDay, (diffDays(s, e) + 1) * pxPerDay)
                const late = st !== 'done' && isoDay(t.dueDate) && isoDay(t.dueDate) < today
                const p = projById.get(t.projectId)
                const col = p?.color ?? '#D4A843'
                const hasDue = !!isoDay(t.dueDate)
                return (
                  <div key={t.id} className="flex items-stretch border-b border-[#F2F1EC] hover:bg-[#FAFAF8]">
                    <div style={{ width: LEFT_W }} className="flex-shrink-0 px-3 py-2 border-r border-[#EDEBE5] flex items-center">
                      <span className="text-[12px] text-[#4A4844] truncate" title={t.title}>{t.title}</span>
                    </div>
                    <div className="relative py-2" style={{ width }}>
                      {hasDue ? (
                        <button
                          onClick={() => { if (didDrag.current) { didDrag.current = false; return } onOpenTask(t.id) }}
                          onPointerDown={isLead && !busy ? (ev) => { ev.currentTarget.setPointerCapture(ev.pointerId); didDrag.current = false; setDrag({ id: t.id, startX: ev.clientX, deltaDays: 0 }) } : undefined}
                          onPointerMove={isLead && drag?.id === t.id ? (ev) => { const dx = ev.clientX - drag.startX; const dd = Math.abs(dx) < DRAG_THRESHOLD ? 0 : Math.round(dx / pxPerDay); if (dd !== drag.deltaDays) { if (dd !== 0) didDrag.current = true; setDrag({ ...drag, deltaDays: dd }) } } : undefined}
                          onPointerUp={isLead && drag?.id === t.id ? () => finishDrag(t) : undefined}
                          onPointerCancel={isLead ? () => { setDrag(null); didDrag.current = false } : undefined}
                          title={`${t.title} — ${fmtDay(s)}→${fmtDay(e)} · ${STATUS_META[st].label}${late ? ' · TELAT' : ''}${isLead ? ' · seret ujung kanan untuk geser deadline' : ''}`}
                          className="absolute h-5 rounded-md overflow-hidden flex items-center"
                          style={{ left, width: Math.max(pxPerDay, barW + (drag?.id === t.id ? drag.deltaDays * pxPerDay : 0)), top: 6, backgroundColor: `${col}33`, boxShadow: late ? 'inset 0 0 0 1.5px #C0362C' : 'none', cursor: isLead ? 'ew-resize' : 'pointer', touchAction: 'none' }}
                        >
                          <span className="h-full" style={{ width: `${PROGRESS[st] * 100}%`, backgroundColor: col }} />
                          <span className="absolute inset-0 flex items-center px-1.5 text-[10px] font-semibold text-[#1A1916] truncate">{barW > 60 ? t.title : ''}</span>
                        </button>
                      ) : (
                        <button onClick={() => onOpenTask(t.id)} title={`${t.title} — tanpa deadline`} className="absolute w-3 h-3 rounded-full" style={{ left: xOf(s), top: 8, backgroundColor: col, opacity: 0.5 }} />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Toggle<T extends string>({ value, onChange, opts }: { value: T; onChange: (v: T) => void; opts: { v: T; label: string; Icon?: typeof FolderKanban }[] }) {
  return (
    <div className="inline-flex rounded-lg border border-[#E7E5DF] p-0.5 bg-[#F7F6F2]">
      {opts.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-semibold transition-colors ${value === o.v ? 'bg-white text-[#1A1916] shadow-sm' : 'text-[#8B8A85] hover:text-[#1A1916]'}`}
        >
          {o.Icon && <o.Icon className="w-3.5 h-3.5" />} {o.label}
        </button>
      ))}
    </div>
  )
}
